import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=logged
  const [studio, setStudio] = useState(null);

  const loadStudio = useCallback(async () => {
    try {
      const { data } = await api.get("/studio");
      setStudio(data);
      return data;
    } catch {
      setStudio(null);
      return null;
    }
  }, []);

  const bootstrap = useCallback(async () => {
    const [meResult, studioResult] = await Promise.allSettled([
      api.get("/auth/me"),
      api.get("/studio"),
    ]);

    if (meResult.status !== "fulfilled") {
      setUser(false);
      setStudio(null);
      return;
    }

    const currentUser = meResult.value.data;
    setUser(currentUser);

    if (currentUser?.onboarding_completed && studioResult.status === "fulfilled") {
      setStudio(studioResult.value.data);
    } else {
      setStudio(null);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    if (data?.onboarding_completed) await loadStudio();
    return data;
  };

  const register = async (name, email, password, inviteCode) => {
    const { data } = await api.post("/auth/register", { name, email, password, invite_code: inviteCode });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setStudio(null);
  };

  const refreshUser = bootstrap;

  return (
    <AuthContext.Provider value={{ user, studio, login, register, logout, refreshUser, loadStudio, setStudio }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiError };
